<script lang="ts">
  // Fullscreen drill-down for a single diff. Two view modes:
  //
  //   triple : baseline / current / diff laid out as full-width rows,
  //            scrollable for long mobile-viewport stories.
  //   slider : baseline + current stacked with a draggable vertical divider
  //            (Chromatic-style "rub to compare"). Diff image hidden in
  //            this mode — the slider is itself the comparison artifact.
  //
  // Keyboard: j/↓/space = next diff, k/↑ = prev, a = accept, c = cycle mode,
  // Esc = close. Adjacent images preloaded for jump-to-jump snappiness.

  import type { DiffRow } from "../lib/api";

  let {
    diffs,
    index,
    onClose,
    onNavigate,
    onAccept,
    busyKey,
  }: {
    diffs: DiffRow[];
    index: number;
    onClose: () => void;
    onNavigate: (idx: number) => void;
    onAccept: (story: string) => Promise<void> | void;
    busyKey: string | null;
  } = $props();

  let mode = $state<"triple" | "slider">("triple");
  let sliderPct = $state(50);
  let dragging = false;

  const current = $derived(diffs[index]);

  function go(delta: number) {
    const n = diffs.length;
    if (n === 0) return;
    const next = (index + delta + n) % n;
    onNavigate(next);
  }

  function onKey(e: KeyboardEvent) {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "j" || e.key === "ArrowDown" || e.key === "ArrowRight" || e.key === " ") {
      e.preventDefault();
      go(1);
    } else if (e.key === "k" || e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      go(-1);
    } else if (e.key === "a") {
      e.preventDefault();
      if (current && current.review_state !== "accepted") void onAccept(current.id);
    } else if (e.key === "c") {
      e.preventDefault();
      mode = mode === "triple" ? "slider" : "triple";
    }
  }

  $effect(() => {
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Preload neighbouring diff PNGs so j/k feels instant.
  $effect(() => {
    const neighbours = [-1, 1, 2].map((d) => diffs[(index + d + diffs.length) % diffs.length]);
    for (const n of neighbours) {
      if (!n) continue;
      for (const url of [n.baseline_url, n.current_url, n.diff_url]) {
        if (url) {
          const img = new Image();
          img.src = url;
        }
      }
    }
  });

  function startDrag(e: PointerEvent) {
    dragging = true;
    (e.target as Element).setPointerCapture(e.pointerId);
    updateSlider(e);
  }
  function moveDrag(e: PointerEvent) {
    if (!dragging) return;
    updateSlider(e);
  }
  function endDrag(e: PointerEvent) {
    dragging = false;
    try {
      (e.target as Element).releasePointerCapture(e.pointerId);
    } catch {}
  }
  function updateSlider(e: PointerEvent) {
    const host = (e.currentTarget as HTMLElement) ?? (e.target as HTMLElement);
    const rect = host.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    sliderPct = Math.max(0, Math.min(100, pct));
  }
</script>

<div
  class="overlay"
  role="dialog"
  aria-modal="true"
  onclick={(e) => { if (e.target === e.currentTarget) onClose(); }}
>
  <div class="modal">
    <header>
      <button class="icon" title="prev (k / ↑)" onclick={() => go(-1)} aria-label="prev">‹</button>
      <span class="counter">{index + 1} / {diffs.length}</span>
      <button class="icon" title="next (j / ↓ / space)" onclick={() => go(1)} aria-label="next">›</button>
      <div class="title">
        <span class={`pill ${current?.state ?? ""}`}>{current?.state}</span>
        <span>{current?.story_title || current?.story_id}</span>
        <span class="vp">· {current?.viewport} · {current?.theme}</span>
      </div>
      <div class="spacer"></div>
      <div class="mode">
        <button class:active={mode === "triple"} onclick={() => (mode = "triple")} title="triple view">▦</button>
        <button class:active={mode === "slider"} onclick={() => (mode = "slider")} title="slider view (c)">⇆</button>
      </div>
      {#if current && current.state !== "unchanged"}
        <button
          class="accept"
          disabled={current.review_state === "accepted" || busyKey !== null}
          onclick={() => onAccept(current.id)}
          title="accept this diff (a)"
        >{current.review_state === "accepted" ? "✓ accepted" : "accept"}</button>
      {/if}
      <button class="icon close" title="close (Esc)" onclick={onClose} aria-label="close">×</button>
    </header>

    {#if !current}
      <p class="muted">no diff at index {index}</p>
    {:else if mode === "triple"}
      <section class="triple-view">
        {#if current.baseline_url}
          <figure>
            <figcaption>baseline</figcaption>
            <img src={current.baseline_url} alt="baseline" />
          </figure>
        {/if}
        {#if current.current_url}
          <figure>
            <figcaption>current</figcaption>
            <img src={current.current_url} alt="current" />
          </figure>
        {/if}
        {#if current.diff_url}
          <figure>
            <figcaption>diff</figcaption>
            <img src={current.diff_url} alt="diff" />
          </figure>
        {/if}
        {#if current.pixel_delta > 0}
          <p class="delta">
            Δ {current.pixel_delta.toLocaleString()} px
            ({(current.pixel_delta_pct * 100).toFixed(3)}%)
          </p>
        {/if}
      </section>
    {:else if mode === "slider"}
      {#if current.baseline_url && current.current_url}
        <section
          class="slider-view"
          onpointerdown={startDrag}
          onpointermove={moveDrag}
          onpointerup={endDrag}
          onpointercancel={endDrag}
        >
          <img class="under" src={current.baseline_url} alt="baseline" />
          <div class="over" style={`clip-path: inset(0 ${100 - sliderPct}% 0 0);`}>
            <img src={current.current_url} alt="current" />
          </div>
          <div class="handle" style={`left: ${sliderPct}%`}>
            <div class="handle-bar"></div>
            <div class="handle-knob">⇆</div>
            <div class="labels">
              <span class="lbl-left">baseline</span>
              <span class="lbl-right">current</span>
            </div>
          </div>
        </section>
      {:else}
        <p class="muted">slider view needs both baseline and current — this diff is {current.state}.</p>
      {/if}
    {/if}

    <footer class="shortcuts">
      <kbd>j</kbd>/<kbd>k</kbd> nav · <kbd>a</kbd> accept · <kbd>c</kbd> mode · <kbd>Esc</kbd> close
    </footer>
  </div>
</div>

<style>
  .overlay {
    position: fixed;
    inset: 0;
    background: rgba(7, 9, 13, 0.92);
    z-index: 1000;
    display: grid;
    place-items: stretch;
    padding: 16px;
    overflow-y: auto;
  }
  .modal {
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    background: #131722;
    border: 1px solid #1c2230;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border-bottom: 1px solid #1c2230;
    flex-wrap: wrap;
  }
  header .title {
    display: flex;
    gap: 8px;
    align-items: center;
    font-size: 14px;
  }
  header .vp { color: #6b7180; font-size: 12px; }
  header .spacer { flex: 1; }
  .icon {
    background: transparent;
    border: 1px solid #2a3142;
    color: #e6e8ee;
    border-radius: 6px;
    width: 30px;
    height: 30px;
    font-size: 16px;
    cursor: pointer;
  }
  .icon.close { font-size: 20px; }
  .counter { font-size: 12px; color: #9aa1ad; min-width: 60px; text-align: center; }
  .mode { display: flex; gap: 4px; }
  .mode button {
    background: transparent;
    border: 1px solid #2a3142;
    color: #9aa1ad;
    border-radius: 6px;
    width: 30px;
    height: 30px;
    cursor: pointer;
  }
  .mode button.active { color: #7df9c0; border-color: #7df9c0; }
  .accept {
    background: #1c2230;
    border: 1px solid #2a3142;
    color: #7df9c0;
    border-radius: 6px;
    padding: 6px 14px;
    cursor: pointer;
    font-size: 13px;
  }
  .accept:disabled { color: #6b7180; border-color: #1c2230; cursor: default; }
  .triple-view {
    padding: 16px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    overflow-y: auto;
  }
  .triple-view figure {
    margin: 0;
    background: #0f1218;
    border: 1px solid #1c2230;
    border-radius: 8px;
    overflow: hidden;
  }
  .triple-view figcaption {
    font-size: 11px;
    color: #6b7180;
    padding: 6px 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .triple-view img {
    width: 100%;
    display: block;
  }
  .triple-view .delta {
    color: #f7b955;
    font-size: 12px;
    margin: 0;
    padding: 4px 12px;
  }
  .slider-view {
    position: relative;
    margin: 16px;
    border: 1px solid #1c2230;
    border-radius: 8px;
    overflow: hidden;
    background: #0f1218;
    touch-action: none;
    cursor: ew-resize;
    user-select: none;
  }
  .slider-view img.under,
  .slider-view .over img {
    width: 100%;
    display: block;
  }
  .slider-view .over {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
  .slider-view .handle {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #7df9c0;
    transform: translateX(-1px);
    pointer-events: none;
  }
  .slider-view .handle-bar {
    position: absolute;
    inset: 0;
    background: #7df9c0;
    width: 2px;
  }
  .slider-view .handle-knob {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 32px;
    height: 32px;
    background: #7df9c0;
    color: #0f1218;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 16px;
    font-weight: bold;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  }
  .slider-view .labels {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateY(-50%);
    font-size: 10px;
    color: #6b7180;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    white-space: nowrap;
    pointer-events: none;
  }
  .slider-view .lbl-left { position: absolute; right: 32px; top: -8px; }
  .slider-view .lbl-right { position: absolute; left: 32px; top: -8px; }
  .shortcuts {
    padding: 8px 14px;
    border-top: 1px solid #1c2230;
    font-size: 11px;
    color: #6b7180;
    text-align: center;
  }
  .shortcuts kbd {
    background: #1c2230;
    border: 1px solid #2a3142;
    border-radius: 4px;
    padding: 1px 6px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11px;
    margin: 0 2px;
  }
  .pill {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    background: #1c2230;
  }
  .pill.changed { color: #f7b955; }
  .pill.added   { color: #7df9c0; }
  .pill.removed { color: #ff7c7c; }
  .muted { color: #6b7180; padding: 24px; text-align: center; }
</style>
